import { useState } from "react";
import { Menu, MenuContent, MenuItem, MenuTrigger } from "@norkron/web-menu";
import { api } from "../api";
import { ChevronDown, FileText } from "../compose/Icon";
import type { ExportFormat, ExportGate } from "../api/types";
import { describeGate, downloadExport, type DownloadDeps } from "../lib/exportDownload";

/**
 * Download menu on the document viewer — offers the two export renditions
 * (Word `.docx` / PDF) from `GET /api/documents/:id/export?format=…`.
 *
 * ── THE BUG THIS SHAPE EXISTS TO PREVENT ───────────────────────────────────
 * The export terminal answers a refusal with a JSON body (`403
 * delivery_gate_blocked` when a required reviewer has not signed off on the head
 * content hash, `403 forbidden` without `export_deliver`, `500 server_error`
 * when the renderer itself fails). **A native `<a download>` cannot inspect a
 * status code** — the browser writes whatever bytes arrive to disk under the
 * requested name. So a blocked export was saved as `Report.pdf`: a 299-byte JSON
 * file wearing a PDF extension. Both formats share the route, which is why PDF
 * and DOCX failed identically.
 *
 * The server is RIGHT to answer that way (a machine-readable refusal is correct
 * for a programmatic client, and the delivery gate must stay server-derived —
 * architecture invariant 6). What was wrong is that the client offered an
 * affordance it could not honour. Two layers fix it, and both are needed:
 *
 *   1. PRE-FLIGHT. `gate` is the SERVER's own decision, carried on the document
 *      detail and computed by the SAME `api.export_policy.decide` the export
 *      terminal enforces with. When it refuses, the items render as DISABLED
 *      menu items with NO `href` AT ALL — so no left-click, ⌘-click,
 *      middle-click, keyboard Enter or right-click "Save link as" can reach a
 *      route that would refuse. The reason (which reviewer is missing, or which
 *      capability) is shown on the item itself.
 *   2. GUARDED PRIMARY CLICK. The pre-flight cannot predict everything: a
 *      verdict can be stranded by an edit between page load and click, and a
 *      RENDER failure is not predictable at all. So a plain left-click on a
 *      permitted item is intercepted, fetched, and the bytes are saved ONLY
 *      after a verified 2xx.
 *
 * Neither layer can cause a download the server did not authorise — the terminal
 * re-decides on every request. They can only refuse to write a refusal to disk.
 *
 * ── THE ITEM IS STILL THE ANCHOR WHEN EXPORT IS PERMITTED ──────────────────
 * `<MenuItem asChild>` makes our `<a href download>` BE the `role="menuitem"`
 * element. Nesting a focusable `<a>` inside a lib-rendered `<div
 * role="menuitem">` would be a WAI-ARIA violation (a menuitem must not contain
 * focusable descendants) and would split activation in two: the mouse would hit
 * the anchor while keyboard Enter went only to `onSelect`, forcing a
 * programmatic `.click()` that races the portal unmount and can swallow the
 * download. `asChild` collapses both paths onto the one real anchor.
 *
 * That anchor is DELIBERATELY KEPT, `href` and all: ⌘/middle-click "open in new
 * tab" and right-click "Save link as" keep working — affordances a purely
 * JS-triggered download would silently lose. The guard intercepts ONLY the plain
 * primary click (main button, no modifier), so the native paths fall through
 * untouched; the pre-flight is what makes them safe, because a refusing export
 * has no anchor for them to act on in the first place.
 *
 * ── WHY IT PORTALS ─────────────────────────────────────────────────────────
 * Same reason as `<ArticleRowMenu>`: the viewer's `.docview__bar` is
 * `position: sticky` with a `z-index`, which creates a stacking context, and
 * `.shell__main` above it is `overflow: auto`. An absolutely-positioned panel
 * is confined to both. `<MenuContent>` portals to `document.body` and is placed
 * `fixed` against the trigger by `@norkron/web-floating`, flipping above the
 * trigger when there is no room below.
 *
 * The dropdown closes on outside-click and Escape (capture-phase, topmost-layer
 * only, via `@norkron/web-dismiss`) and returns focus to the trigger.
 */

/** The two renditions, in menu order. */
const RENDITIONS: ReadonlyArray<{
  format: ExportFormat;
  label: string;
  ext: string;
  testid: string;
}> = [
  { format: "docx", label: "Word document", ext: ".docx", testid: "download-docx" },
  { format: "pdf", label: "PDF", ext: ".pdf", testid: "download-pdf" },
];

export function DownloadMenu({
  docId,
  title,
  gate,
  defaultOpen = false,
  onRefusal,
  downloadDeps,
}: {
  docId: string;
  /**
   * Document title — the fallback filename when the response carries no
   * `Content-Disposition`. The server always sends one; this is belt-and-braces.
   */
  title?: string;
  /**
   * The SERVER's export decision from the document detail. Absent/`null` (an
   * older server, or a caller that has not loaded it) → offer the download and
   * let the guarded fetch catch any refusal. Never a grant: it can only remove
   * an affordance, never create one.
   */
  gate?: ExportGate | null;
  /** Initial open state (used by tests / controlled callers). */
  defaultOpen?: boolean;
  /** Surface a refusal the guard caught after the click (the viewer passes a toast). */
  onRefusal?: (message: string) => void;
  /** Injectable fetch/save seam so tests can assert nothing was written to disk. */
  downloadDeps?: DownloadDeps;
}) {
  // Controlled: an uncontrolled <Menu> fires `onOpenChange` twice per
  // transition (it drives both useDisclosure and useControlledState).
  const [open, setOpen] = useState(defaultOpen);
  const [busy, setBusy] = useState(false);

  // The server's reason, or null when the export is permitted (or unknown).
  const refusal = describeGate(gate);

  /**
   * Intercept ONLY the plain primary click. A modified click
   * (⌘/ctrl/shift/alt) or a non-main button is the user asking for the
   * BROWSER's behaviour — open in a new tab, save link as — so it falls
   * through to the native anchor untouched.
   */
  async function onAnchorClick(
    e: React.MouseEvent<HTMLAnchorElement>,
    format: ExportFormat,
  ): Promise<void> {
    if (e.defaultPrevented) return;
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const fallback = `${title?.trim() || "document"}.${format}`;
      const result = await downloadExport(api.exportUrl(docId, format), fallback, downloadDeps);
      if (!result.ok) onRefusal?.(result.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Menu
      // `MenuContent` takes no `data-testid` (its props are zod-stripped), so
      // the <Menu> id is what gives the surface a stable `…-content` handle.
      id="download-menu"
      open={open}
      onOpenChange={setOpen}
      placement="bottom-end"
    >
      <MenuTrigger asChild ariaLabel="Download">
        <button
          type="button"
          className="btn btn--secondary btn--sm"
          data-testid="download-menu-trigger"
        >
          <FileText size={16} strokeWidth={1.5} />
          Download
          <ChevronDown size={14} strokeWidth={1.5} />
        </button>
      </MenuTrigger>

      <MenuContent className="ev-menu__surface" ariaLabel="Download format">
        {RENDITIONS.map(({ format, label, ext, testid }) =>
          refusal !== null ? (
            // REFUSED: a plain disabled menu item. No anchor, so there is no
            // `href` for ANY activation path to save a JSON refusal from. `id`
            // is the handle (`data-testid` would be zod-stripped off a
            // non-`asChild` MenuItem).
            <MenuItem
              key={format}
              id={testid}
              label={label}
              description={refusal}
              disabled
              closeOnSelect={false}
            />
          ) : (
            <MenuItem key={format} asChild label={label}>
              <a
                className="ev-menu__item"
                data-testid={testid}
                href={api.exportUrl(docId, format)}
                download
                aria-busy={busy || undefined}
                onClick={(e) => void onAnchorClick(e, format)}
              >
                {label} <small>{ext}</small>
              </a>
            </MenuItem>
          ),
        )}
      </MenuContent>
    </Menu>
  );
}
